import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtCurrency, fmtDateTime } from "@/lib/utils-domain";
import { Play, Stop, Receipt, DownloadSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TimeBilling() {
    const { user, setUser } = useAuth();
    const [cases, setCases] = useState([]);
    const [entries, setEntries] = useState([]);
    const [active, setActive] = useState(null);
    const [selectedCase, setSelectedCase] = useState("");
    const [description, setDescription] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [genOpen, setGenOpen] = useState(false);
    const [activeInvoice, setActiveInvoice] = useState(null);

    const load = async () => {
        const [c, e, a, i] = await Promise.all([
            api.get("/cases"),
            api.get("/time-entries"),
            api.get("/time-entries/active"),
            api.get("/invoices"),
        ]);
        setCases(c.data); setEntries(e.data); setActive(a.data || null); setInvoices(i.data);
        if (c.data.length > 0 && !selectedCase) setSelectedCase(c.data[0].case_id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const start = async () => {
        if (!selectedCase) { toast.error("Pick a case"); return; }
        try {
            const { data } = await api.post("/time-entries/start", { case_id: selectedCase, description });
            setActive(data);
            setDescription("");
            toast.success("Timer started");
        } catch { toast.error("Failed to start timer"); }
    };
    const stop = async () => {
        if (!active) return;
        await api.post("/time-entries/stop", { entry_id: active.entry_id });
        toast.success("Logged");
        setActive(null);
        load();
    };

    const totalAmount = useMemo(() => entries.reduce((s, e) => s + (e.amount || 0), 0), [entries]);

    const updateRate = async (rate) => {
        const { data } = await api.patch("/auth/profile", { hourly_rate: parseFloat(rate) || 0 });
        setUser(data);
        toast.success("Rate updated");
        load();
    };

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Time & Billing</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight">Track and bill.</h1>
                    <p className="text-sm text-slate-500 mt-2">Rate: <span className="font-mono font-semibold text-slate-900">{fmtCurrency(user?.hourly_rate)}/hr</span></p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setGenOpen(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="generate-invoice-button">
                        <Receipt size={14} weight="duotone" /> Generate invoice
                    </button>
                </div>
            </div>

            {/* Timer card */}
            <div className="card-sharp p-6 mb-6" data-testid="timer-card">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Live timer</div>
                {active ? (
                    <ActiveTimerBlock active={active} cases={cases} rate={user?.hourly_rate || 300} onStop={stop} />
                ) : (
                    <div className="grid lg:grid-cols-[1fr_2fr_auto] gap-3 items-end">
                        <div>
                            <label className="label-sharp">Case</label>
                            <select value={selectedCase} onChange={(e) => setSelectedCase(e.target.value)} className="input-sharp" data-testid="timer-case-select">
                                {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">Description (optional)</label>
                            <input value={description} onChange={(e) => setDescription(e.target.value)} className="input-sharp" placeholder="What are you working on?" data-testid="timer-description-input" />
                        </div>
                        <button onClick={start} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="start-timer-button">
                            <Play size={14} weight="fill" /> Start
                        </button>
                    </div>
                )}
            </div>

            {/* Rate editor */}
            <div className="card-sharp p-5 mb-6 flex items-center gap-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Hourly rate</div>
                <input
                    type="number"
                    defaultValue={user?.hourly_rate}
                    onBlur={(e) => updateRate(e.target.value)}
                    className="input-sharp w-32 font-mono"
                    data-testid="rate-input"
                />
                <div className="text-xs text-slate-500">Applied to all new billable amounts.</div>
            </div>

            {/* Entries */}
            <div className="card-sharp mb-6">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                    <div className="font-display font-bold">Time log</div>
                    <div className="text-sm text-slate-500 font-mono">Total: <span className="text-slate-900 font-semibold">{fmtCurrency(totalAmount)}</span></div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                            <th className="px-5 py-3">Case</th><th className="px-5 py-3">Description</th>
                            <th className="px-5 py-3">Started</th>
                            <th className="px-5 py-3 text-right">Minutes</th>
                            <th className="px-5 py-3 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {entries.filter((e) => e.stopped_at).map((e) => {
                            const c = cases.find((x) => x.case_id === e.case_id);
                            return (
                                <tr key={e.entry_id}>
                                    <td className="px-5 py-3 text-slate-800">{c?.name || "—"}</td>
                                    <td className="px-5 py-3 text-slate-500">{e.description || "—"}</td>
                                    <td className="px-5 py-3 text-slate-500 font-mono text-xs">{fmtDateTime(e.started_at)}</td>
                                    <td className="px-5 py-3 text-right font-mono">{e.minutes}</td>
                                    <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(e.amount)}</td>
                                </tr>
                            );
                        })}
                        {entries.filter((e) => e.stopped_at).length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No time logged yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Invoices */}
            <div className="card-sharp">
                <div className="px-5 py-4 border-b border-slate-200 font-display font-bold">Invoices</div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                            <th className="px-5 py-3">Invoice</th><th className="px-5 py-3">Client</th>
                            <th className="px-5 py-3">Issued</th>
                            <th className="px-5 py-3 text-right">Amount</th><th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {invoices.map((i) => (
                            <tr key={i.invoice_id}>
                                <td className="px-5 py-3 font-mono text-xs">{i.invoice_id.slice(-8).toUpperCase()}</td>
                                <td className="px-5 py-3">{i.client_name}</td>
                                <td className="px-5 py-3 text-slate-500 font-mono text-xs">{fmtDateTime(i.issued_at)}</td>
                                <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(i.total)}</td>
                                <td className="px-5 py-3 text-right">
                                    <button onClick={() => setActiveInvoice(i)} className="text-xs text-[#0B2447] font-semibold hover:underline" data-testid={`view-invoice-${i.invoice_id}`}>View</button>
                                </td>
                            </tr>
                        ))}
                        {invoices.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No invoices yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {genOpen && <GenerateInvoiceModal cases={cases} entries={entries} onClose={() => setGenOpen(false)} onCreated={(inv) => { setGenOpen(false); load(); setActiveInvoice(inv); }} />}
            {activeInvoice && <InvoiceViewer invoice={activeInvoice} rate={user?.hourly_rate || 300} onClose={() => setActiveInvoice(null)} />}
        </div>
    );
}

function ActiveTimerBlock({ active, cases, rate, onStop }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
    const started = new Date(active.started_at).getTime();
    const sec = Math.floor((now - started) / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const fmt = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    const c = cases.find((x) => x.case_id === active.case_id);
    const amount = (sec / 3600) * rate;
    return (
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
                <div className="text-xs text-slate-500 font-mono">Working on</div>
                <div className="font-display text-lg font-bold mt-1">{c?.name || "—"}</div>
                {active.description && <div className="text-sm text-slate-600 mt-1">{active.description}</div>}
            </div>
            <div className="text-center">
                <div className="font-mono text-4xl font-bold tracking-tight text-[#0B2447]" data-testid="timer-display">{fmt}</div>
                <div className="text-xs text-slate-500 font-mono mt-1">{fmtCurrency(amount)} billable</div>
            </div>
            <button onClick={onStop} className="bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-medium transition" data-testid="stop-timer-main">
                <Stop size={14} weight="fill" /> Stop & log
            </button>
        </div>
    );
}

function GenerateInvoiceModal({ cases, entries, onClose, onCreated }) {
    const [caseId, setCaseId] = useState(cases[0]?.case_id || "");
    const candidates = entries.filter((e) => e.case_id === caseId && e.stopped_at);
    const [selected, setSelected] = useState({});
    useEffect(() => {
        const all = {};
        candidates.forEach((e) => { all[e.entry_id] = true; });
        setSelected(all);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);
    const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const total = candidates.filter((e) => selected[e.entry_id]).reduce((s, e) => s + (e.amount || 0), 0);

    const submit = async () => {
        const entry_ids = Object.keys(selected).filter((k) => selected[k]);
        if (entry_ids.length === 0) { toast.error("Pick at least one entry"); return; }
        const theCase = cases.find((c) => c.case_id === caseId);
        try {
            const { data } = await api.post("/invoices", { case_id: caseId, client_id: theCase.client_id, entry_ids });
            toast.success("Invoice generated");
            onCreated(data);
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="bg-white max-w-2xl w-full p-7 rounded-sm border border-slate-200 shadow-xl animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="generate-invoice-modal">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold">Generate invoice</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="label-sharp">Case</label>
                        <select value={caseId} onChange={(e) => setCaseId(e.target.value)} className="input-sharp" data-testid="invoice-case-select">
                            {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="label-sharp">Time entries</label>
                        <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-64 overflow-y-auto">
                            {candidates.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">No billable entries on this case.</div>}
                            {candidates.map((e) => (
                                <label key={e.entry_id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer">
                                    <input type="checkbox" checked={!!selected[e.entry_id]} onChange={() => toggle(e.entry_id)} data-testid={`invoice-entry-${e.entry_id}`} />
                                    <div className="flex-1">
                                        <div className="text-sm">{e.description || "—"}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{fmtDateTime(e.started_at)} · {e.minutes} min</div>
                                    </div>
                                    <div className="text-sm font-mono font-semibold">{fmtCurrency(e.amount)}</div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                        <div className="text-sm text-slate-500">Estimated total</div>
                        <div className="font-display text-2xl font-bold">{fmtCurrency(total)}</div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="invoice-submit-button">Create invoice</button>
                </div>
            </div>
        </div>
    );
}

function InvoiceViewer({ invoice, rate, onClose }) {
    const download = () => {
        const lines = [
            `INVOICE  ${invoice.invoice_id.slice(-8).toUpperCase()}`,
            ``,
            `From:    ${invoice.firm_name}`,
            `To:      ${invoice.client_name}`,
            `Matter:  ${invoice.case_name}`,
            `Issued:  ${new Date(invoice.issued_at).toLocaleDateString()}`,
            `Due:     ${new Date(invoice.due_at).toLocaleDateString()}`,
            ``,
            `LINE ITEMS`,
            ...invoice.entries_snapshot.map((e, i) => `  ${i + 1}. ${(e.description || "Billable work").padEnd(40)}  ${String(e.minutes).padStart(4)} min  $${((e.minutes/60) * (invoice.hourly_rate || rate)).toFixed(2)}`),
            ``,
            `Hourly rate:     $${(invoice.hourly_rate || rate).toFixed(2)}/hr`,
            `Total minutes:   ${invoice.total_minutes}`,
            `Subtotal:        $${invoice.subtotal.toFixed(2)}`,
            `Tax:             $${invoice.tax.toFixed(2)}`,
            `TOTAL DUE:       $${invoice.total.toFixed(2)}`,
            ``,
            `Thank you for your business.`,
        ].join("\n");
        const blob = new Blob([lines], { type: "text/plain" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `Invoice_${invoice.invoice_id.slice(-8)}.txt`;
        a.click();
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="bg-white max-w-2xl w-full rounded-sm border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto" data-testid="invoice-viewer">
                <div className="px-7 py-5 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Invoice</div>
                        <div className="font-display text-2xl font-bold mt-1">{invoice.invoice_id.slice(-8).toUpperCase()}</div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={download} className="btn-primary inline-flex items-center gap-2" data-testid="invoice-download-button">
                            <DownloadSimple size={14} /> Download
                        </button>
                        <button onClick={onClose} className="btn-secondary"><X size={14} /></button>
                    </div>
                </div>
                <div className="p-7 space-y-6">
                    <div className="grid grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">From</div>
                            <div className="font-semibold">{invoice.firm_name}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">Bill to</div>
                            <div className="font-semibold">{invoice.client_name}</div>
                            <div className="text-slate-500 mt-1">Matter: {invoice.case_name}</div>
                        </div>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="border-y border-slate-200">
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                                <th className="py-2">Item</th><th className="py-2 text-right">Min</th><th className="py-2 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invoice.entries_snapshot.map((e, i) => (
                                <tr key={i}>
                                    <td className="py-3">{e.description || "Billable work"}</td>
                                    <td className="py-3 text-right font-mono">{e.minutes}</td>
                                    <td className="py-3 text-right font-mono">{fmtCurrency((e.minutes/60) * (invoice.hourly_rate || rate))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="border-t border-slate-200 pt-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{fmtCurrency(invoice.subtotal)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-mono">{fmtCurrency(invoice.tax)}</span></div>
                        <div className="flex justify-between text-lg font-display font-bold border-t border-slate-200 pt-2">
                            <span>Total due</span><span className="font-mono">{fmtCurrency(invoice.total)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
